import React, { Component } from 'react';
import { connect } from 'react-redux';
import axios from 'axios';
import Auth from '../../../auth/auth';
import { importSchema, fetchUserList, fetchTechOwnerList } from '../data-dictionary.actions';
import Step1DataSource from './steps/step1-data-source';
import Step2SchemaSelection from './steps/step2-schema-selection';
import { buildItemId, buildColumnId } from './item-id';
import Step3BusinessContext from './steps/step4-business-context';
import Step4ReviewSave from './steps/step5-review-save';
import Button from '../../../components/button';
import Modal from '../../../components/modal';
import Snackbar from '@material-ui/core/Snackbar';


// Single source of truth for the step sequence. The count used to be a literal 5 repeated
// across handleNext, render and the draft clamp; dropping the AI Enhancement step is
// exactly the change that leaves one of those behind.
const STEP_TITLES = [
  'Choose Data Source',
  'Schema Selection',
  'Business Context',
  'Review & Save',
];
const STEP_COUNT = STEP_TITLES.length;

export class DDWizard extends Component {
  constructor(props) {
    super(props);
    
    // Check if we're editing an existing dictionary
    const isEditing = !!props.dictionaryId;
    
    // Only load saved state if not editing (for new dictionaries in progress)
    const savedState = !isEditing ? this.loadFromLocalStorage() : null;

    const defaults = {
      currentStep: 1,
      // Step 1 data
      selectedConnection: null,
      // Step 2 data
      schemaMetadata: null,
      fullSchemaData: null, // Raw API response with full details
      selectedSchema: null,
      selectedDatabase: null,
      selectedItems: {
        schemas: [],
        tables: [],
        views: [],
        storedProcedures: []
      },
      selectedAttributes: {},
      // Step 3 data
      businessContextData: {},
      // Step 4 data
      dictionaryName: '',
      dictionaryDescription: '',
      // UI state
      isLoading: false,
      showCancelModal: false,
      error: null,
      snackbarStatus: false,
      loadedFromAPI: false // Track if we've loaded details from API
    };

    // Merge rather than replace. A draft saved by an older build has no key for a
    // field added since (selectedDatabase was one), and the persisted set is now
    // deliberately a subset — replacing wholesale would leave those undefined.
    this.state = savedState ? { ...defaults, ...savedState } : defaults;

    // A connection-sourced draft is restored without its schema. Steps 3-4 read
    // fullSchemaData, and buildFullSchemaPayload returns null without it — which
    // throws in buildImportSchemaPayload. Send them back to step 2, which refetches
    // on mount; every selection and everything they typed is still restored.
    if (savedState && !this.state.fullSchemaData && !this.state.schemaMetadata) {
      this.state = { ...this.state, currentStep: Math.min(this.state.currentStep || 1, 2) };
      this.pendingSelectionPrune = true;
    }

    // The wizard used to have five steps; the AI Enhancement step is gone. A draft from
    // an older build that persisted a truthy schemaMetadata skips the clamp above and can
    // still carry currentStep 5, where renderStep falls to `default: null` and
    // stepTitles[4] is undefined — a blank body under a Save button.
    if (savedState && this.state.currentStep > STEP_COUNT) {
      this.state = { ...this.state, currentStep: STEP_COUNT };
    }

    this.autoSaveInterval = null;
  }

  // Restored selections are keyed by POSITION (item-id.js: `${schema}_${type}_${index}`),
  // and they are about to be re-applied against a freshly fetched schema. A table
  // added or dropped at the source shifts every later index, so an id that no longer
  // exists must be dropped rather than silently re-pointed at a different object.
  //
  // businessContextData holds BOTH item ids and column ids (`${itemId}_col_${n}`), so
  // membership is tested on the base item id — matching column keys literally against
  // the metadata would delete every column-level note on each resume.
  pruneRestoredSelections = (schemaMetadata) => {
    const knownIds = new Set();
    Object.values(schemaMetadata || {}).forEach((items) => {
      if (Array.isArray(items)) items.forEach((item) => item && item.id && knownIds.add(item.id));
    });
    if (knownIds.size === 0) return null;

    const baseItemId = (key) => {
      const at = key.indexOf('_col_');
      return at === -1 ? key : key.slice(0, at);
    };

    const selectedItems = {};
    Object.entries(this.state.selectedItems || {}).forEach(([type, ids]) => {
      selectedItems[type] = Array.isArray(ids) ? ids.filter((id) => knownIds.has(id)) : ids;
    });

    const businessContextData = {};
    Object.entries(this.state.businessContextData || {}).forEach(([key, value]) => {
      if (knownIds.has(baseItemId(key))) businessContextData[key] = value;
    });

    return { selectedItems, businessContextData };
  }

  componentDidMount() {
    // Auto-save every 30 seconds (but not for edit mode)
    if (!this.props.dictionaryId) {
      this.autoSaveInterval = setInterval(() => {
        this.saveToLocalStorage();
      }, 30000);
    }
    
    // If we have dictionary details already loaded, populate the wizard
    if (this.props.dictionaryDetails && !this.state.loadedFromAPI) {
      this.populateWizardFromDetails(this.props.dictionaryDetails);
    }
  }

  componentDidUpdate(prevProps) {
    // Check if dictionary details were just loaded
    if (this.props.dictionaryDetails && 
        this.props.dictionaryDetails !== prevProps.dictionaryDetails && 
        !this.state.loadedFromAPI) {
      this.populateWizardFromDetails(this.props.dictionaryDetails);
    }
  }

  componentWillUnmount() {
    if (this.autoSaveInterval) {
      clearInterval(this.autoSaveInterval);
    }
  }

  // The introspected schema is deliberately NOT persisted. It is the largest thing
  // in state by orders of magnitude, this runs on every object checkbox toggle
  // (updateStepData) and every 30s, and serializing it made each click stall on a
  // multi-megabyte JSON.stringify — then usually blow the ~5MB localStorage quota,
  // so the cost was paid and nothing was stored.
  // It is re-fetchable from the connection + schema, which step 2 already does on
  // mount when schemaMetadata is absent.
  saveToLocalStorage = () => {
    const {
      currentStep, selectedConnection, selectedSchema, selectedDatabase,
      selectedItems, selectedAttributes,
      businessContextData, dictionaryName, dictionaryDescription,
    } = this.state;

    const persisted = {
      currentStep, selectedConnection, selectedSchema, selectedDatabase,
      selectedItems, selectedAttributes,
      businessContextData, dictionaryName, dictionaryDescription,
    };

    try {
      localStorage.setItem('ddWizardState', JSON.stringify(persisted));
    } catch (e) {
      // Quota or serialization failure. Losing the draft is recoverable; taking the
      // wizard down with it is not.
    }
  }

  loadFromLocalStorage = () => {
    try {
      const saved = localStorage.getItem('ddWizardState');
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (e) {
      // Corrupt or foreign payload — start clean rather than half-restored.
    }
    return null;
  }

  clearLocalStorage = () => {
    try {
      localStorage.removeItem('ddWizardState');
    } catch (e) {
      console.error('Failed to clear wizard state', e);
    }
  }

  resetWizard = () => {
    this.clearLocalStorage();
    this.setState({
      currentStep: 1,
      selectedConnection: null,
      schemaMetadata: null,
      fullSchemaData: null,
      selectedSchema: null,
      selectedDatabase: null,
      selectedItems: {
        schemas: [],
        tables: [],
        views: [],
        storedProcedures: []
      },
      businessContextData: {},
      dictionaryName: '',
      dictionaryDescription: '',
      isLoading: false,
      showCancelModal: false,
      error: null
    });
  }

  handleNext = () => {
    if (this.state.currentStep < STEP_COUNT) {
      const nextStep = this.state.currentStep + 1;

      // Moving from schema selection: auto-select all available attributes
      if (this.state.currentStep === 2) {
        this.autoSelectAllAttributes();
      }

      this.setState({ currentStep: nextStep }, () => {
        // Scroll to top when navigating to next step
        window.scrollTo({ top: 0, behavior: 'smooth' });
        this.saveToLocalStorage();
      });
    }
  }

  extractAttributeIdsFromData = (data, type) => {
    if (!Array.isArray(data) || data.length === 0) return [];

    const sampleItem = data[0];
    return Object.keys(sampleItem)
      .filter(key => key !== 'columns')
      .map(key => `${type}_${key}`);
  }

  autoSelectAllAttributes = () => {
    const { fullSchemaData } = this.state;

    if (!fullSchemaData) {
      this.setState({ selectedAttributes: {} });
      return;
    }

    const selectedAttributes = {};

    if (Array.isArray(fullSchemaData.tables) && fullSchemaData.tables.length > 0) {
      selectedAttributes['Table Attributes'] = this.extractAttributeIdsFromData(fullSchemaData.tables, 'table');
      const firstTableWithColumns = fullSchemaData.tables.find(t => Array.isArray(t.columns) && t.columns.length > 0);
      if (firstTableWithColumns) {
        selectedAttributes['Column Attributes'] = this.extractAttributeIdsFromData(firstTableWithColumns.columns, 'column');
      }
    } else if (Array.isArray(fullSchemaData.views) && fullSchemaData.views.length > 0) {
      const firstViewWithColumns = fullSchemaData.views.find(v => Array.isArray(v.columns) && v.columns.length > 0);
      if (firstViewWithColumns) {
        selectedAttributes['Column Attributes'] = this.extractAttributeIdsFromData(firstViewWithColumns.columns, 'column');
      }
    }

    if (Array.isArray(fullSchemaData.views) && fullSchemaData.views.length > 0) {
      selectedAttributes['View Attributes'] = this.extractAttributeIdsFromData(fullSchemaData.views, 'view');
    }

    if (Array.isArray(fullSchemaData.storedProcedures) && fullSchemaData.storedProcedures.length > 0) {
      selectedAttributes['Stored Procedure Attributes'] = this.extractAttributeIdsFromData(fullSchemaData.storedProcedures, 'storedProcedure');
    }

    Object.keys(fullSchemaData).forEach((categoryKey) => {
      if (categoryKey === 'tables' || categoryKey === 'views' || categoryKey === 'storedProcedures') {
        return;
      }

      const items = fullSchemaData[categoryKey];
      if (Array.isArray(items) && items.length > 0) {
        const categoryLabel = this.getAttributeCategoryForItemType(categoryKey);
        const typePrefix = categoryKey.endsWith('s') ? categoryKey.slice(0, -1) : categoryKey;
        selectedAttributes[categoryLabel] = this.extractAttributeIdsFromData(items, typePrefix);
      }
    });

    this.setState({ selectedAttributes });
  }

  handleBack = () => {
    if (this.state.currentStep > 1) {
      this.setState({ currentStep: this.state.currentStep - 1 }, () => {
        // Scroll to top when navigating back
        window.scrollTo({ top: 0, behavior: 'smooth' });
        this.saveToLocalStorage();
      });
    }
  }

  handleCancel = () => {
    this.setState({ showCancelModal: true });
  }

  confirmCancel = () => {
    this.clearLocalStorage();
    this.setState({ showCancelModal: false });
    this.props.onCancel();
  }

  cancelCancel = () => {
    this.setState({ showCancelModal: false });
  }

  updateStepData = (data) => {
    // Step 2 reports the refetched schema through here. That is the first moment a
    // restored draft's positional ids can be checked against real data, so the prune
    // runs here rather than in the constructor — and here rather than inside Step 2,
    // which never receives businessContextData.
    let next = data;
    if (this.pendingSelectionPrune && data && data.schemaMetadata) {
      this.pendingSelectionPrune = false;
      const pruned = this.pruneRestoredSelections(data.schemaMetadata);
      if (pruned) next = { ...data, ...pruned };
    }

    this.setState(next, () => {
      if (!this.props.dictionaryId) {
        this.saveToLocalStorage();
      }
    });
  }

  populateWizardFromDetails = (details) => {
    // console.log('Populating wizard from API details:', details);
    
    if (!details || !details.instance) {
      // console.warn('Invalid dictionary details received');
      return;
    }

    // Extract instance information
    const instance = details.instance;
    
    // Build selectedConnection object from instance
    const selectedConnection = {
      connId: instance.instanceId,
      connectionName: instance.instanceName,
      name: instance.instanceName,
      type: instance.instanceType,
      dbType: instance.instanceType,
      host: instance.host,
      server: instance.host,
      port: instance.port,
      environment: instance.environment
    };

    // Build fullSchemaData from API response
    const fullSchemaData = {
      tables: details.tables || [],
      views: details.views || [],
      storedProcedures: details.storedProcedures || []
    };

    // Build schemaMetadata for step 2 (simplified view of items)
    const schemaMetadata = {
      tables: (details.tables || []).map((table, index) => ({
        id: buildItemId(table, 'tables', index, details.schemaName),
        name: table.tableName || table.name,
        schema: table.schema || table.schemaName || details.schemaName,
        type: 'table'
      })),
      views: (details.views || []).map((view, index) => ({
        id: buildItemId(view, 'views', index, details.schemaName),
        name: view.viewName || view.name,
        schema: view.schema || view.schemaName || details.schemaName,
        type: 'view'
      })),
      storedProcedures: (details.storedProcedures || []).map((sp, index) => ({
        id: buildItemId(sp, 'storedProcedures', index, details.schemaName),
        name: sp.procedureName || sp.name,
        schema: sp.schema || sp.schemaName || details.schemaName,
        type: 'storedProcedure'
      }))
    };

    // Build selectedItems (all items are selected since they were saved)
    const selectedItems = {
      tables: schemaMetadata.tables.map(t => t.id),
      views: schemaMetadata.views.map(v => v.id),
      storedProcedures: schemaMetadata.storedProcedures.map(sp => sp.id)
    };

    // Build businessContextData from saved business context
    const businessContextData = {};
    
    // Process tables
    (details.tables || []).forEach((table, index) => {
      const itemId = buildItemId(table, 'tables', index, details.schemaName);
      if (table.businessContext) {
        businessContextData[itemId] = {
          businessContext: table.businessContext
        };
      }
      // Process columns
      if (table.columns) {
        table.columns.forEach((col, colIndex) => {
          const colId = buildColumnId(itemId, colIndex);
          if (col.businessContext) {
            businessContextData[colId] = {
              businessContext: col.businessContext
            };
          }
        });
      }
    });

    // Process views
    (details.views || []).forEach((view, index) => {
      const itemId = buildItemId(view, 'views', index, details.schemaName);
      if (view.businessContext) {
        businessContextData[itemId] = {
          businessContext: view.businessContext
        };
      }
      // Process columns
      if (view.columns) {
        view.columns.forEach((col, colIndex) => {
          const colId = buildColumnId(itemId, colIndex);
          if (col.businessContext) {
            businessContextData[colId] = {
              businessContext: col.businessContext
            };
          }
        });
      }
    });

    // Process stored procedures
    (details.storedProcedures || []).forEach((sp, index) => {
      const itemId = buildItemId(sp, 'storedProcedures', index, details.schemaName);
      if (sp.businessContext) {
        businessContextData[itemId] = {
          businessContext: sp.businessContext
        };
      }
    });

    // Update state with all loaded data
    this.setState({
      selectedConnection,
      fullSchemaData,
      schemaMetadata,
      selectedItems,
      selectedSchema: details.schemaName || null,
      selectedDatabase: details.databaseName || details.schemaName || null,
      businessContextData,
      dictionaryName: instance.instanceName,
      dictionaryDescription: instance.description || '',
      loadedFromAPI: true,
      currentStep: 3 // Jump to step 3 (Business Context) to view/edit
    });

    // console.log('Wizard populated with loaded data');
  }

  findItemInSchema = (itemId) => {
    if (!this.state.fullSchemaData) return null;
    
    // Search through all schema data to find the item
    const schemas = this.state.fullSchemaData.schemas || [];
    for (const schema of schemas) {
      // Check tables
      if (schema.tables) {
        const table = schema.tables.find(t => t.id === itemId || t.name === itemId);
        if (table) return { ...table, type: 'table', schema: schema.name };
      }
      // Check views
      if (schema.views) {
        const view = schema.views.find(v => v.id === itemId || v.name === itemId);
        if (view) return { ...view, type: 'view', schema: schema.name };
      }
      // Check stored procedures
      if (schema.storedProcedures) {
        const sp = schema.storedProcedures.find(s => s.id === itemId || s.name === itemId);
        if (sp) return { ...sp, type: 'storedProcedure', schema: schema.name };
      }
    }
    return null;
  }

  buildFullSchemaPayload = () => {
    const { fullSchemaData, selectedItems, businessContextData, selectedAttributes } = this.state;
    
    if (!fullSchemaData) return null;

    const enrichedSchema = {};

    // Process each category in fullSchemaData
    Object.keys(fullSchemaData).forEach(categoryKey => {
      const items = fullSchemaData[categoryKey];
      
      if (!Array.isArray(items)) {
        enrichedSchema[categoryKey] = items;
        return;
      }

      // Get selected IDs for this category
      const selectedIds = selectedItems[categoryKey] || [];
      
      // Enrich each item with businessContext, attributes, and included flag
      enrichedSchema[categoryKey] = items.map((item, index) => {
        // Must match the id step 2 generated, or nothing is ever marked included.
        const itemId = buildItemId(item, categoryKey, index);
        const isIncluded = selectedIds.includes(itemId);
        
        // Get business context for this item
        const contextData = businessContextData[itemId] || {};
        
        // Log owner data for debugging
        // if (isIncluded && contextData.owner) {
        //   console.log(`Owner data for ${categoryKey} item ${itemId}:`, contextData.owner);
        // }
        
        // Build the enriched item
        const enrichedItem = {
          ...item,
          included: isIncluded,
          businessContext: contextData.businessContext || '',
          businessOwner: (contextData.owner && Array.isArray(contextData.owner) && contextData.owner.length > 0) ? contextData.owner : null,
          techOwner: (contextData.techOwner && Array.isArray(contextData.techOwner) && contextData.techOwner.length > 0) ? contextData.techOwner : null,
          attributes: {}
        };

        // Add selected attributes for this item type
        const attributeCategory = this.getAttributeCategoryForItemType(categoryKey);
        if (attributeCategory && selectedAttributes[attributeCategory]) {
          selectedAttributes[attributeCategory].forEach(attrId => {
            const fieldName = attrId.split('_').slice(1).join('_');
            if (item[fieldName] !== undefined) {
              enrichedItem.attributes[fieldName] = item[fieldName];
            }
          });
        }

        // Process columns if they exist
        if (item.columns && Array.isArray(item.columns)) {
          enrichedItem.columns = item.columns.map((col, colIndex) => {
            const colId = buildColumnId(itemId, colIndex);
            const colContextData = businessContextData[colId] || {};
            
            const enrichedColumn = {
              ...col,
              businessContext: colContextData.businessContext || '',
              isPii: !!colContextData.isPii,
              isAiEligible: !!colContextData.isAiEligible,
              attributes: {}
            };

            // Add selected column attributes
            if (selectedAttributes['Column Attributes']) {
              selectedAttributes['Column Attributes'].forEach(attrId => {
                const fieldName = attrId.split('_').slice(1).join('_');
                if (col[fieldName] !== undefined) {
                  enrichedColumn.attributes[fieldName] = col[fieldName];
                }
              });
            }

            return enrichedColumn;
          });
        }

        return enrichedItem;
      });
    });

    return enrichedSchema;
  }

  // Lower-cased names of dictionaries that already exist, used for the
  // duplicate-name check on Step 5 (issue #610). Reuses the list the parent
  // page already loads into Redux — no extra API call needed.
  getExistingDictionaryNames = () => {
    const currentId = this.props.dictionaryId;
    return (this.props.dictionaryList || [])
      // In edit mode, don't let the dictionary flag its own (unchanged) name.
      .filter((d) => !currentId || (d.instanceId ?? d.instance_id ?? d.id) !== currentId)
      .map((d) => d.instanceName || d.instance_name || d.name || d.dictionaryName || '')
      .filter(Boolean)
      .map((n) => n.trim().toLowerCase());
  }

  getAttributeCategoryForItemType = (itemType) => {
    const mapping = {
      tables: 'Table Attributes',
      views: 'View Attributes',
      storedProcedures: 'Stored Procedure Attributes',
      functions: 'Functions Attributes',
      triggers: 'Triggers Attributes',
      sequences: 'Sequences Attributes',
      synonyms: 'Synonyms Attributes',
      packages: 'Packages Attributes',
      userDefinedTypes: 'User Defined Types Attributes'
    };
    
    // Return mapped value or generate dynamically
    return mapping[itemType] || (itemType
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, str => str.toUpperCase())
      .trim() + ' Attributes');
  }

  handleSave = async () => {
    // Scroll to top to show the snackbar
    window.scrollTo({ top: 0, behavior: 'smooth' });
    
    this.setState({ snackbarStatus: true, error: null });
    
    try {
      // console.log('\n=== BUSINESS CONTEXT DATA BEFORE SAVE ===');
      // console.log('businessContextData:', JSON.stringify(this.state.businessContextData, null, 2));
      // console.log('=========================================\n');
      
      const fullSchema = this.buildFullSchemaPayload();
      const importPayload = this.buildImportSchemaPayload(fullSchema);

      // Use axios directly for immediate response handling
      const auth = new Auth();
      
      const response = await axios.post(
        process.env.REACT_APP_API_URL + '/dataDictionary/importSchema', 
        importPayload, 
        {
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'x-access-token': auth.getAccessToken()
          },
          timeout: 5 * 60 * 1000
        }
      );

      // console.log('=== Import Schema Response ===');
      // console.log(response.data);
      // console.log('==============================');

      if (response.data && response.data.status) {
        // The import succeeded, but subordinate metadata (foreign keys, indexes,
        // triggers, unique constraints) may have been unreadable. That used to be
        // swallowed server-side, so a dictionary with zero relationships looked
        // like a clean import — surface it instead.
        const warnings = (response.data.response && response.data.response.metadataWarnings) || [];
        this.clearLocalStorage();
        this.setState({
          snackbarStatus: false,
          error: warnings.length > 0
            ? `Dictionary created, but some metadata could not be imported: ${warnings.join(' ')}`
            : null,
        });
        this.props.onSave(importPayload);
      } else {
        throw new Error(response.data?.error || 'Failed to import schema');
      }
      
    } catch (error) {
      // console.error('Save error:', error);
      this.setState({ 
        snackbarStatus: false, 
        error: error.response?.data?.error || error.message || 'Failed to save dictionary'
      });
    }
  }

  buildImportSchemaPayload = (fullSchema) => {
    const { selectedConnection, dictionaryName, dictionaryDescription,
      selectedSchema, selectedDatabase } = this.state;
    const schemaName = selectedSchema || 'default';
    // Still an array on the wire. The model imports N schemas per instance and must
    // keep doing so for dictionaries created before one-schema-per-import; sending
    // a one-element array means nothing server-side had to change.
    const schemaNames = [schemaName];
    // Connection target. Same as the schema on single-level engines; on MS SQL
    // Server it is the database the schema lives in.
    const databaseName = selectedDatabase || schemaName;

    // Build instance object - use dictionaryName from step 6
    const instance = {
      instanceName: (dictionaryName || '').trim() || 'Untitled Dictionary',
      instanceType: selectedConnection?.type || selectedConnection?.dbType || 'Unknown',
      host: selectedConnection?.host || selectedConnection?.server || 'localhost',
      port: selectedConnection?.port || 3306,
      description: dictionaryDescription || '',
      environment: selectedConnection?.environment || 'Development',
      dbConnectionId: selectedConnection?.connId || null,
    };

    // Filter only included items and format for API
    const formatItems = (items) => {
      if (!items || !Array.isArray(items)) return [];
      
      return items
        .filter(item => item.included)
        .map(item => ({
          ...item,
          businessContext: item.businessContext || '',
          businessOwner: item.businessOwner || null,
          techOwner: item.techOwner || null,
          attributes: {
            ...(item.attributes || {}),
            businessContext: item.businessContext || '',
            businessOwner: item.businessOwner || null,
            techOwner: item.techOwner || null
          },
          columns: item.columns ? item.columns.map(col => ({
            ...col,
            businessContext: col.businessContext || '',
            attributes: col.attributes || {}
          })) : []
        }));
    };

    return {
      instance,
      databaseName,
      schemaNames,
      // Retained so anything still reading the singular field keeps working.
      schemaName,
      tables: formatItems(fullSchema.tables),
      views: formatItems(fullSchema.views),
      storedProcedures: formatItems(fullSchema.storedProcedures),
    };
  }

  mockSave = (payload) => {
    return new Promise((resolve) => {
      setTimeout(() => {
        // Save to localStorage temporarily
        try {
          const list = JSON.parse(localStorage.getItem('dataDictionaryList') || '[]');
          list.push({
            ...payload,
            id: Date.now(),
            createdAt: new Date().toISOString()
          });
          localStorage.setItem('dataDictionaryList', JSON.stringify(list));
        } catch (e) {
          // console.error('Error saving to localStorage', e);
        }
        resolve();
      }, 1000);
    });
  }

  calculateCompletionStats = () => {
    const allItems = [
      ...Object.values(this.state.selectedItems).flat()
    ];
    
    let fullyDocumented = 0;
    let partiallyDocumented = 0;
    let notDocumented = 0;

    allItems.forEach(itemId => {
      const context = this.state.businessContextData[itemId];
      if (context && context.businessContext && context.businessContext.trim()) {
        fullyDocumented++;
      } else {
        notDocumented++;
      }
    });

    const total = allItems.length;
    const completionPercentage = total > 0 ? Math.round((fullyDocumented / total) * 100) : 0;

    return {
      total,
      fullyDocumented,
      partiallyDocumented,
      notDocumented,
      completionPercentage
    };
  }

  canProceedFromStep = (step) => {
    switch (step) {
      case 1:
        return !!this.state.selectedConnection;
      case 2: {
        const selectedCount = Object.entries(this.state.selectedItems || {}).reduce((sum, [key, items]) => {
          if (key === 'schemas') return sum;
          return sum + (Array.isArray(items) ? items.length : 0);
        }, 0);
        // A schema that could not be read would silently produce an incomplete
        // dictionary — its objects, and any foreign keys into them, would simply be
        // missing. Block until it is retried or removed from the selection.
        const hasUnreadableSchema = (this.props.failedSchemas || []).length > 0;
        return !!this.state.selectedSchema && selectedCount > 0 && !hasUnreadableSchema;
      }
      // Step 3 (Business Context) is optional — the `default` below lets it through.
      case 4: {
        const name = (this.state.dictionaryName || '').trim();
        if (!name) return false;
        // Block saving a dictionary whose name already exists (issue #610).
        return !this.getExistingDictionaryNames().includes(name.toLowerCase());
      }
      default:
        return true;
    }
  }

  renderStep() {
    switch (this.state.currentStep) {
      case 1:
        return (
          <Step1DataSource
            selectedConnection={this.state.selectedConnection}
            selectedSchema={this.state.selectedSchema}
            onUpdate={this.updateStepData}
          />
        );
      case 2:
        return (
          <Step2SchemaSelection
            selectedConnection={this.state.selectedConnection}
            schemaMetadata={this.state.schemaMetadata}
            fullSchemaData={this.state.fullSchemaData}
            selectedSchema={this.state.selectedSchema}
            // Two-level (MS SQL) selection. Without these the picker cannot see the
            // database it just set, and the schema dropdown stays on
            // "Pick a database first" even after the schemas have loaded.
            selectedDatabase={this.state.selectedDatabase}
            // Re-entering step 2 on a dictionary that already exists must not show
            // that dictionary's own schema as taken by itself.
            currentDictionaryName={this.state.loadedFromAPI ? this.state.dictionaryName : null}
            selectedItems={this.state.selectedItems}
            onUpdate={this.updateStepData}
          />
        );
      case 3:
        return (
          <Step3BusinessContext
            selectedItems={this.state.selectedItems}
            selectedAttributes={this.state.selectedAttributes}
            schemaMetadata={this.state.schemaMetadata}
            fullSchemaData={this.state.fullSchemaData}
            businessContextData={this.state.businessContextData}
            onUpdate={this.updateStepData}
            userList={this.props.userList}
            fetchUserList={this.props.fetchUserList}
            techOwnerList={this.props.techOwnerList}
            fetchTechOwnerList={this.props.fetchTechOwnerList}
          />
        );
      case 4:
        return (
          <Step4ReviewSave
            dictionaryName={this.state.dictionaryName}
            dictionaryDescription={this.state.dictionaryDescription}
            selectedConnection={this.state.selectedConnection}
            selectedItems={this.state.selectedItems}
            selectedAttributes={this.state.selectedAttributes}
            businessContextData={this.state.businessContextData}
            completionStats={this.calculateCompletionStats()}
            existingNames={this.getExistingDictionaryNames()}
            onUpdate={this.updateStepData}
            error={this.state.error}
          />
        );
      default:
        return null;
    }
  }

  render() {
    const { currentStep } = this.state;
    const canProceed = this.canProceedFromStep(currentStep);
    const stepTitles = STEP_TITLES;

    return (
      <div className="dd-wizard-container" data-testid="dd-wizard" data-current-step={this.state.currentStep}>
        <Snackbar
          open={this.state.snackbarStatus}
          message="Saving. Please wait..."
          className="snack-bar"
        />

        {/* Step Indicator */}
        <div className="dd-wizard-steps">
          {stepTitles.map((title, index) => {
            const stepNumber = index + 1;
            const isActive = currentStep === stepNumber;
            const isCompleted = currentStep > stepNumber;
            const isPending = currentStep < stepNumber;
            
            return (
              <div
                key={index}
                className={`dd-wizard-step ${
                  isActive ? 'active' : ''
                } ${
                  isCompleted ? 'completed' : ''
                } ${
                  isPending ? 'pending' : ''
                }`}
              >
                <div className="dd-wizard-step-number">
                  {isCompleted ? (
                    <span className="material-icons">check</span>
                  ) : (
                    stepNumber
                  )}
                </div>
                {index < stepTitles.length - 1 && (
                  <div className="dd-wizard-step-connector" />
                )}
                <div className="dd-wizard-step-label">
                  <div className="dd-wizard-step-title">{title}</div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Page Title */}
        <h2 className="dd-wizard-page-title">
          {stepTitles[currentStep - 1]}
        </h2>

        {/* Step Content */}
        <div className="dd-wizard-content">
          {this.renderStep()}
        </div>

        {/* Navigation Buttons */}
        <div className="dd-wizard-actions">
          <div className="dd-wizard-actions-left">
            <Button
              label="Cancel"
              className="btn btn-default"
              onClick={this.handleCancel}
              data-testid="dd-wizard-cancel"
            />
          </div>
          <div className="dd-wizard-actions-right">
            {currentStep > 1 && (
              <Button
                label="Back"
                className="btn btn-default"
                onClick={this.handleBack}
                style={{ marginRight: 12 }}
                data-testid="dd-wizard-back"
              />
            )}
            {currentStep < STEP_COUNT ? (
              <Button
                label="Next"
                className="btn btn-action"
                onClick={this.handleNext}
                disabled={!canProceed}
                data-testid="dd-wizard-next"
              />
            ) : (
              <Button
                label="Save"
                className="btn btn-action"
                onClick={this.handleSave}
                disabled={!canProceed}
                data-testid="dd-wizard-save"
              />
            )}
          </div>
        </div>

        {/* Cancel Confirmation Modal */}
        <Modal
          open={this.state.showCancelModal}
          close={this.cancelCancel}
          confirm={this.confirmCancel}
          title="Cancel Dictionary Creation"
          content="Are you sure? Unsaved changes will be lost."
          primaryButton="Yes, Cancel"
          secondaryButton="No, Continue"
        />
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  dictionaryList: state.dataDictionaryReducer?.dictionaryList || [],
  importSchemaResponse: state.dataDictionaryReducer?.importSchemaResponse || null,
  importSchemaLoading: state.dataDictionaryReducer?.importSchemaLoading || false,
  importSchemaError: state.dataDictionaryReducer?.importSchemaError || null,
  // Non-empty when a selected schema could not be read; blocks Next (canProceedFromStep).
  failedSchemas: state.dataDictionaryReducer?.failedSchemas || [],
  userList: state.dataDictionaryReducer?.userList || [],
  techOwnerList: state.dataDictionaryReducer?.techOwnerList || []
});

const mapDispatchToProps = {
  importSchema,
  fetchUserList,
  fetchTechOwnerList
};

export default connect(mapStateToProps, mapDispatchToProps)(DDWizard);
